import React, { useState, useEffect } from 'react';
import { Form, Button, Card, message, Spin, Space } from 'antd';
import { TestItemSelector } from '../../components/TestItemSelector/TestItemSelector';
import { testRunService } from '../../services/testRunService';
import { testItemService } from '../../services/testItemService';
import { TestItem } from '../../types/testItem';
import { TestRunItem } from '../../types/testRun';
import { useTranslation } from 'react-i18next';

interface AddTestItemsFormProps {
    testRunId: number;
    projectId: number;
    onSuccess: () => void;
    onCancel: () => void;
}

export const AddTestItemsForm: React.FC<AddTestItemsFormProps> = ({
    testRunId,
    projectId,
    onSuccess,
    onCancel
}) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [selectedItemIds, setSelectedItemIds] = useState<number[]>([]);
    const [existingItems, setExistingItems] = useState<TestRunItem[]>([]);
    const [loadingItems, setLoadingItems] = useState(true);

    useEffect(() => {
        loadExistingItems();
    }, [testRunId]);

    const loadExistingItems = async () => {
        try {
            setLoadingItems(true);
            const testRun = await testRunService.getTestRun(testRunId);
            setExistingItems(testRun.test_run_items || []);
            setSelectedItemIds(testRun.test_run_items?.map((item: TestRunItem) => item.test_item_id) || []);
        } catch (error) {
            console.error('Error loading existing items:', error);
            message.error(t('testItems.notifications.loadItemsError'));
        } finally {
            setLoadingItems(false);
        }
    };

    const handleSelectionChange = (selectedIds: number[]) => {
        setSelectedItemIds(selectedIds);
    };

    const handleSubmit = async () => {
        try {
            setLoading(true);
            
            // Find items to add and remove
            const existingIds = existingItems.map(item => item.test_item_id);
            const itemsToAdd = selectedItemIds.filter(id => !existingIds.includes(id));
            const itemsToRemove = existingIds.filter(id => !selectedItemIds.includes(id));

            // Add new items
            if (itemsToAdd.length > 0) {
                const itemsToAddData = itemsToAdd.map(id => ({
                    test_item_id: id
                }));
                await testRunService.addTestItems(testRunId, itemsToAddData);
            }

            // Remove items
            for (const itemId of itemsToRemove) {
                const testRunItem = existingItems.find(item => item.test_item_id === itemId);
                if (testRunItem) {
                    await testRunService.removeTestItem(testRunId, testRunItem.id);
                }
            }

            message.success(t('testItems.notifications.updateItemsSuccess'));
            onSuccess();
        } catch (error) {
            console.error('Error updating test items:', error);
            message.error(t('testItems.notifications.updateItemsError'));
        } finally {
            setLoading(false);
        }
    };

    if (loadingItems) {
        return (
            <div style={{ textAlign: 'center', padding: '20px' }}>
                <Spin size="large" />
            </div>
        );
    }

    return (
        <Card>
            <Form layout="vertical">
                <Form.Item
                    label="Test Items"
                    required
                    tooltip="Select test items to add to this test run"
                >
                    <TestItemSelector
                        projectId={projectId}
                        onSelectionChange={handleSelectionChange}
                        initialSelection={existingItems.map(item => item.test_item_id)}
                    />
                </Form.Item>

                <Form.Item>
                    <Space.Compact style={{ width: '100%' }}>
                        <Button onClick={onCancel} style={{ width: '50%' }}>
                            Cancel
                        </Button>
                        <Button
                            type="primary"
                            onClick={handleSubmit}
                            loading={loading}
                            style={{ width: '50%' }}
                        >
                            Update Test Items
                        </Button>
                    </Space.Compact>
                </Form.Item>
            </Form>
        </Card>
    );
}; 
 